"use client"

import React from "react"
import Image from "next/image"
import Link from "next/link"
import { motion, MotionValue } from "framer-motion"
import { colorMap } from "@modules/products/components/colorMap"

type LayeredBadgeCardProps = {
  product: {
    id?: string
    title: string
    link?: string
    thumbnail: string
    images?: any[]
    options?: Record<string, string>
  }
  translate: MotionValue<number>
  index: number
  isHovered: boolean
  anyCardHovered: boolean
  onHover: () => void
  onLeave: () => void
}

export const LayeredBadgeCard: React.FC<LayeredBadgeCardProps> = ({
  product,
  translate,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  index,
  isHovered,
  anyCardHovered,
  onHover,
  onLeave,
}) => {
  // Process images to extract option information
  const processedImages = React.useMemo(() => {
    if (!product.images || product.images.length === 0) {
      return []
    }

    return product.images.map((image) => {
      const baseName =
        decodeURIComponent(image.url)
          .replaceAll(/\d{13}\-/g, "")
          .split("/")
          .pop()
          ?.split(".")[0] || ""

      const [optionName, optionValue] = baseName.split("_")

      return {
        ...image,
        baseName,
        optionName,
        optionValue,
      }
    })
  }, [product.images])

  // Filter base images
  const baseImages = React.useMemo(() => {
    return processedImages.filter((image) => image.optionName === "Base")
  }, [processedImages])

  // Get selected base image
  const selectedBaseImage = React.useMemo(() => {
    if (baseImages.length === 0) {
      return null
    }

    // If no options are selected, use the first base image
    if (!product.options || Object.keys(product.options).length === 0) {
      return baseImages[0]
    }

    // For base images, check if the Base option matches the image's optionValue
    const baseOption = product.options["Base"]

    if (baseOption) {
      const filtered = baseImages.filter(
        (image) => baseOption === image.optionValue
      )
      return filtered.length > 0 ? filtered[0] : baseImages[0]
    }

    // If no base option is found, use the first base image
    return baseImages[0]
  }, [baseImages, product.options])

  // Filter color images
  const colorImages = React.useMemo(() => {
    return processedImages.filter((image) =>
      image.optionName?.startsWith("Color")
    )
  }, [processedImages])

  // If we have layered images and options, render them
  const hasLayeredImages =
    product.images &&
    product.options &&
    product.images.length > 0 &&
    selectedBaseImage

  return (
    <Link href={product.link || "#"} className="block h-80 sm:h-96 w-[260px] sm:w-[280px] md:w-[300px] lg:w-[320px] relative flex-shrink-0 cursor-pointer">
      <motion.div
        style={{
          x: translate,
          transform: `scale(${isHovered ? 1.02 : 1})`,
          filter: `brightness(${isHovered ? 105 : anyCardHovered ? 40 : 100}%)`,
          transition: "filter 300ms ease-out, transform 300ms ease-out",
        }}
        animate={{
          y: isHovered ? -10 : 0,
        }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        key={product.id || product.title}
        className="h-full w-full relative"
      >
        {/* Typography corner markers */}
        <div className={`absolute top-0 left-0 w-[15px] h-[15px] border-l border-t border-white/20 z-10 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-30"}`}></div>
        <div className={`absolute top-0 right-0 w-[15px] h-[15px] border-r border-t border-white/20 z-10 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-30"}`}></div>
        <div className={`absolute bottom-0 left-0 w-[15px] h-[15px] border-l border-b border-white/20 z-10 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-30"}`}></div>
        <div className={`absolute bottom-0 right-0 w-[15px] h-[15px] border-r border-b border-white/20 z-10 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-30"}`}></div>

        {/* Index marker */}
        <div className={`absolute top-4 left-4 text-[8px] text-white/30 font-mono transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-30"}`}>
          {(index + 1).toString().padStart(2, '0')}
        </div>
        {hasLayeredImages ? (
          <div className="relative h-[15rem] sm:h-[20rem] w-full mx-auto p-2 flex items-center justify-center">
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Base image */}
              <Image
                src={selectedBaseImage.url}
                className="object-contain object-center"
                alt={product.title}
                unoptimized
                fill
              />

              {/* Color overlays */}
              {colorImages.map((image) => {
                // Get the selected option for this color
                const optionName = image.optionName
                const selectedOption = product.options?.[optionName]
                const colorFromMap = selectedOption
                  ? colorMap[selectedOption] || selectedOption
                  : undefined

                if (!selectedOption) {
                  return null
                }

                return (
                  <div
                    key={image.id}
                    className="absolute inset-0 overflow-hidden flex items-center justify-center"
                    style={{
                      backgroundColor: colorFromMap,
                      maskRepeat: "no-repeat",
                      maskPosition: "center",
                      maskSize: "contain",
                      mixBlendMode: "multiply",
                      maskImage: `url('${image.url.replace(" ", "%20")}')`,
                    }}
                  />
                )
              })}
            </div>
          </div>
        ) : (
          // Fallback to thumbnail if no layered images
          <div className="relative h-full w-full p-2 mx-auto flex items-center justify-center">
            <Image
              src={product.thumbnail}
              className="object-contain object-center"
              alt={product.title}
              fill
              unoptimized
            />
          </div>
        )}

        {/* Hover effect overlay */}
        <div
          className={`absolute inset-0 w-full h-full opacity-0 transition-opacity duration-300 p-1 ${
            isHovered ? "opacity-100" : ""
          }`}
          style={{
            background:
              "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 25%, transparent 50%)",
            mixBlendMode: "soft-light",
          }}
        />

        {/* Title overlay */}
        <h2
          className={`max-w-[calc(100%-32px)] border-b-4 border-white/90 py-1 flex items-center justify-start absolute bottom-4 left-4 text-white z-10 transition-opacity duration-300 ${
            isHovered ? "opacity-100" : "opacity-0"
          }`}
        >
          {product.title}
        </h2>
      </motion.div>
    </Link>
  )
}
